import { noop } from "lodash";
import { FC, PropsWithChildren, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AccountCircle, Group } from "@livechat/design-system-icons";
import {
  Avatar,
  DesignToken,
  IPickerListItem,
  Icon,
  IconSource,
  Picker,
  Popover,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import Flex from "components/layout/Flex";
import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { Shortcuts } from "components/shortcuts";
import { KeyModifier } from "components/shortcuts/Shortcuts";
import { config } from "config/config";
import { KeyCodeEnum } from "constants/keyCode";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useBreakpoints } from "hooks/use-breakpoints";
import { IAssignmentTeam, ITicket } from "interfaces/entities/ticket";
import { IAgent, selectActiveAgents, selectActiveAgentsWithoutViewers } from "store/entities/agents";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { ITeam } from "store/entities/teams";
import { TicketsViewActions } from "store/views/tickets/actions";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { StyledLink, TicketDetailsCardTitle } from "./TicketDetailsCardTitle";
import {
  AssigneeContainer,
  FollowersContainer,
  NoAgentInfo,
  TeamContainer,
  TeamData,
  UserData,
  Value,
} from "./TicketDetailsResponsibilityStyles";
import { SectionWrapper } from "./styles";

const UNASSIGNED_OPTION_KEY = "unassigned";

const getTeamPickerListItem = ({ id, name, isDefault }: ITeam): IPickerListItem => ({
  key: id,
  name: name,
  customElement: {
    listItemBody: <TeamPickerListItem name={name} isDefault={isDefault} pickerType="single" />,
    selectedItemBody: <TeamPickerSelectedListItem name={name} isDefault={isDefault} pickerType="single" />,
  },
});

const getAgentPickerListItem = ({ id, name, email, avatar }: IAgent, loggedInAgentId?: string): IPickerListItem => ({
  key: id,
  name: name + email,
  customElement: {
    listItemBody: (
      <AgentPickerListItem
        data-testid="select-body-item"
        agentName={name}
        avatarUrl={avatar}
        isLoggedInAgent={id === loggedInAgentId}
        secondaryText={email}
      />
    ),
    selectedItemBody: <AgentPickerSelectedListItem agentName={name} avatarUrl={avatar} />,
  },
});

const getUnassignedAgentPickerListItem = (): IPickerListItem => ({
  key: UNASSIGNED_OPTION_KEY,
  name: "Unassigned",
  customElement: {
    listItemBody: <AgentPickerListItem agentName="Unassigned" avatarUrl="" secondaryText="" />,
    selectedItemBody: <AgentPickerSelectedListItem agentName="Unassigned" avatarUrl="" />,
  },
});

const PopoverContent: FC<PropsWithChildren<{ title: string; icon: IconSource }>> = ({ title, icon, children }) => (
  <Flex padding={withVar(SpacingToken.Spacing3)} column gap={withVar(SpacingToken.Spacing2)} width="315px">
    <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)}>
      <Icon source={icon} size="small" customColor={withVar(DesignToken.ContentBasicPrimary)} />
      <Text bold customColor={withVar(DesignToken.ContentBasicPrimary)}>
        {title}
      </Text>
    </Flex>
    {children}
  </Flex>
);

enum SectionSelects {
  Agent = "agent",
  Team = "team",
  Followers = "followers",
}

interface IProps {
  ticket: ITicket;
  loggedInAgent: IAgent;
  followers: IAgent[];
  teams: ITeam[];
  canManageTicket: boolean;
  assignedAgent: IAgent | undefined;
  assignedTeam: ITeam | undefined;
  replyAddresses: Record<string, IReplyAddress>;
  licenseID: number;
  onTeamChange(teamId: string): void;
  onAgentChange(agentId: string): void;
  isChildTicket: boolean;
  isRecentTicket: boolean;
  opened: boolean;
  onHeaderClick(): void;
}

const getAgentsAssignedToTeamWithLoggedInOnTop = (
  team: IAssignmentTeam | undefined,
  agents: IAgent[],
  loggedInAgentId: string
): IAgent[] => {
  if (!team) return [];

  return agents
    .filter(agent => agent.teamIds.includes(team.id))
    .sort((agentA, agentB) => (agentA.id === loggedInAgentId ? -1 : agentB.id === loggedInAgentId ? 1 : 0));
};

export const TicketDetailsResponsibility: FC<IProps> = ({
  ticket,
  teams,
  canManageTicket,
  assignedAgent,
  assignedTeam,
  followers,
  replyAddresses,
  licenseID,
  loggedInAgent,
  isChildTicket,
  isRecentTicket,
  opened,
  onHeaderClick,
  onTeamChange,
  onAgentChange,
}) => {
  const { isMobile } = useBreakpoints();
  const [openedSelect, setOpenedSelect] = useState<SectionSelects | null>(null);
  const dispatch = useDispatch();

  const activeAgentsWithoutViewers = useSelector(selectActiveAgentsWithoutViewers);
  const activeAgents = useSelector(selectActiveAgents);
  const agentsAssignedToTeam = getAgentsAssignedToTeamWithLoggedInOnTop(
    assignedTeam,
    activeAgentsWithoutViewers,
    loggedInAgent.id
  );
  const replyAddress = assignedTeam?.replyAddressID
    ? replyAddresses[assignedTeam.replyAddressID].email
    : `${licenseID}@${config.defaultMailboxSuffix}`;
  const isViewer = loggedInAgent.permission === Role.Viewer;
  const isAssignedToMe = assignedAgent?.id === loggedInAgent.id;
  const loggedAgentFollowsTicket = followers.some(follower => follower.id === loggedInAgent.id);
  const popoverOffsetSize = isMobile ? 0 : -5;
  const popoverPlacement = isMobile ? undefined : "left-start";

  const openTeamSelect = (): void => {
    setOpenedSelect(SectionSelects.Team);
  };

  const openAgentSelect = (): void => {
    setOpenedSelect(SectionSelects.Agent);
  };

  const openFollowersSelect = (): void => {
    setOpenedSelect(SectionSelects.Followers);
  };

  const closeSelects = (): void => {
    setOpenedSelect(null);
  };

  const selectTeam = (selectedTeam: IPickerListItem[] | null): void => {
    const selectedTeamId = selectedTeam?.[0]?.key || "";
    if (selectedTeamId !== assignedTeam?.id) {
      onTeamChange(selectedTeamId);
    }
    closeSelects();
  };

  const selectAgent = (selectedAgent: IPickerListItem[] | null): void => {
    const agentId = selectedAgent?.[0]?.key;

    if (agentId) {
      if (agentId === UNASSIGNED_OPTION_KEY) {
        onAgentChange("");
      } else {
        onAgentChange(agentId);
      }
    }

    closeSelects();
  };

  const assignMe = (): void => {
    onAgentChange(assignedAgent?.id === loggedInAgent.id ? "" : loggedInAgent.id);
  };

  const addFollower = (followerId: string, shortcutClicked = false): void => {
    dispatch(TicketsViewActions.addFollower({ ticketID: ticket.id, agentID: followerId }));
    trackEngagement("Followers: Follower added", EventPlace.Tickets, {
      selfFollow: followerId === loggedInAgent.id,
      method: shortcutClicked ? "shortcut" : "button",
    });
  };

  const removeFollower = (followerId: string, shortcutClicked = false): void => {
    dispatch(TicketsViewActions.deleteFollower({ ticketID: ticket.id, agentID: followerId }));
    trackEngagement("Followers: Follower deleted", EventPlace.Tickets, {
      selfUnfollow: followerId === loggedInAgent.id,
      method: shortcutClicked ? "shortcut" : "button",
    });
  };

  const handleFollowerSelect = (selectedFollowers: IPickerListItem[] | null): void => {
    if (selectedFollowers) {
      const shouldAddFollower = selectedFollowers.length > followers.length;
      const shouldRemoveFollower = selectedFollowers.length < followers.length;

      if (shouldAddFollower) {
        const followerToAddId = selectedFollowers.find(follower => !followers.some(f => f.id === follower.key))?.key;
        if (followerToAddId) addFollower(followerToAddId);
      }

      if (shouldRemoveFollower) {
        const followerToRemoveId = followers.find(follower => !selectedFollowers.some(f => f.key === follower.id))?.id;
        if (followerToRemoveId) removeFollower(followerToRemoveId);
      }
    }
  };

  const switchFollowing = (shortcutClicked = false): void => {
    if (loggedAgentFollowsTicket) {
      removeFollower(loggedInAgent.id, shortcutClicked);
    } else {
      addFollower(loggedInAgent.id, shortcutClicked);
    }
  };

  const handleHeaderClick = (): void => {
    onHeaderClick();
    closeSelects();
  };

  const shortcutsConfig = {
    [KeyCodeEnum.KeyA]: {
      disableDefaultBehavior: true,
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
      eventHandler:
        canManageTicket && !isChildTicket && isRecentTicket
          ? (): void => {
              openAgentSelect();
              trackEngagement("Tickets: Shortcut used", EventPlace.Tickets, {
                shortcut: "showAssigneeDropdown",
              });
            }
          : noop,
    },
    [KeyCodeEnum.Esc]: {
      disableDefaultBehavior: true,
      shouldNotBlurOnEscapeClick: true,
      eventHandler: (): void => {
        closeSelects();
      },
    },
    [KeyCodeEnum.KeyF]: {
      disableDefaultBehavior: true,
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
      eventHandler:
        !isChildTicket && isRecentTicket
          ? (): void => {
              switchFollowing(true);
            }
          : noop,
    },
  };

  return (
    <SectionWrapper borderTop>
      <AccordionItem title="Responsibility" opened={opened} onHeaderClick={handleHeaderClick}>
        <Shortcuts config={shortcutsConfig}>
          <TicketDetailsCardTitle
            testId="ticket-team"
            title="Team"
            primaryButton={{
              custom: (
                <Popover
                  isVisible={openedSelect === SectionSelects.Team}
                  placement={popoverPlacement}
                  onClose={closeSelects}
                  offsetSize={popoverOffsetSize}
                  triggerRenderer={() => (
                    <StyledLink onClick={openTeamSelect} disabled={!canManageTicket} type="button">
                      Change
                    </StyledLink>
                  )}
                  closeOnEsc={false}
                >
                  <PopoverContent title="Teams" icon={Group}>
                    <Picker
                      data-testid="ticket-team-select"
                      placeholder="Select"
                      options={teams.map(getTeamPickerListItem)}
                      selected={assignedTeam ? [getTeamPickerListItem(assignedTeam)] : []}
                      onSelect={selectTeam}
                      isVisible
                      noSearchResultText="No teams found"
                      maxListHeight={230}
                      hideClearButton
                      useDismissHookProps={{ escapeKey: false }}
                      useClickHookProps={{ ignoreMouse: true }}
                    />
                  </PopoverContent>
                </Popover>
              ),
            }}
          />
          {assignedTeam && (
            <TeamContainer data-testid="assigned-team">
              <Avatar type="text" text={assignedTeam.name} shape="rounded-square" />
              <TeamData>
                <Value>{assignedTeam.name}</Value>
                <Text
                  title={replyAddress}
                  css={textEllipsisStyles}
                  customColor={withVar(DesignToken.ContentBasicDisabled)}
                >
                  {replyAddress}
                </Text>
              </TeamData>
            </TeamContainer>
          )}
          <TicketDetailsCardTitle
            title="Agent"
            testId="ticket-agent"
            primaryButton={{
              custom: (
                <Popover
                  isVisible={openedSelect === SectionSelects.Agent}
                  placement={popoverPlacement}
                  onClose={closeSelects}
                  offsetSize={popoverOffsetSize}
                  triggerRenderer={() => (
                    <StyledLink onClick={openAgentSelect} disabled={!canManageTicket} type="button">
                      {assignedAgent ? "Change" : "Assign"}
                    </StyledLink>
                  )}
                  closeOnEsc={false}
                >
                  <PopoverContent title={`Agents from ${assignedTeam?.name ?? ""}`} icon={AccountCircle}>
                    <Picker
                      placeholder="Select"
                      options={[
                        getUnassignedAgentPickerListItem(),
                        ...agentsAssignedToTeam.map(agent => getAgentPickerListItem(agent, loggedInAgent.id)),
                      ]}
                      selected={[
                        assignedAgent ? getAgentPickerListItem(assignedAgent) : getUnassignedAgentPickerListItem(),
                      ]}
                      onSelect={selectAgent}
                      isVisible
                      noSearchResultText="No agents found"
                      maxListHeight={240}
                      hideClearButton
                      useDismissHookProps={{ escapeKey: false }}
                    />
                  </PopoverContent>
                </Popover>
              ),
            }}
            secondaryButton={{
              label: isAssignedToMe ? "Unassign me" : "Assign me",
              onClick: assignMe,
              disabled: !canManageTicket,
            }}
          />
          <AssigneeContainer data-testid="assigned-agent">
            {assignedAgent ? (
              <>
                <Avatar
                  type={assignedAgent.avatar ? "image" : "text"}
                  src={assignedAgent.avatar ? assignedAgent.avatar : undefined}
                  text={assignedAgent.name ?? assignedAgent.email}
                />
                <UserData>
                  <Value className="fs-mask">{assignedAgent.name}</Value>
                  <Value faded className="fs-mask">
                    {assignedAgent.email}
                  </Value>
                </UserData>
              </>
            ) : (
              <NoAgentInfo>No agent assigned to this ticket.</NoAgentInfo>
            )}
          </AssigneeContainer>
          <TicketDetailsCardTitle
            title={`Followers (${followers.length})`}
            testId="ticket-followers"
            primaryButton={{
              custom: (
                <Popover
                  isVisible={openedSelect === SectionSelects.Followers}
                  placement={popoverPlacement}
                  onClose={closeSelects}
                  offsetSize={popoverOffsetSize}
                  triggerRenderer={() => (
                    <StyledLink
                      onClick={openFollowersSelect}
                      disabled={!canManageTicket || isViewer}
                      type="button"
                      data-testid="edit-followers"
                    >
                      Edit
                    </StyledLink>
                  )}
                  closeOnEsc={false}
                >
                  <PopoverContent title="Followers" icon={AccountCircle}>
                    <Picker
                      placeholder="Select"
                      options={activeAgents.map(agent => getAgentPickerListItem(agent, loggedInAgent.id))}
                      selected={followers.map(agent => getAgentPickerListItem(agent))}
                      onSelect={handleFollowerSelect}
                      isVisible
                      type="multi"
                      maxListHeight={230}
                      hideClearButton
                      noSearchResultText="No agents found"
                      useDismissHookProps={{ escapeKey: false }}
                    />
                  </PopoverContent>
                </Popover>
              ),
            }}
            secondaryButton={{
              label: loggedAgentFollowsTicket ? "Unfollow" : "Follow",
              onClick: switchFollowing,
              disabled: isChildTicket || !isRecentTicket,
            }}
          />
          {followers.length === 0 ? (
            <FollowersContainer data-testid="assigned-followers-nofollowers">
              <NoAgentInfo>No followers assigned to this ticket.</NoAgentInfo>
            </FollowersContainer>
          ) : (
            <>
              {followers.map((follower, i) => (
                <FollowersContainer data-testid={`assigned-followers-${i}`} key={follower.id}>
                  <Avatar
                    type={follower.avatar ? "image" : "text"}
                    src={follower.avatar ? follower.avatar : undefined}
                    text={follower.name ?? follower.email}
                  />
                  <UserData>
                    <Value data-testid={`assigned-followers-name-${i}`}>{follower.name}</Value>
                    <Value faded>{follower.email}</Value>
                  </UserData>
                </FollowersContainer>
              ))}
            </>
          )}
        </Shortcuts>
      </AccordionItem>
    </SectionWrapper>
  );
};
